import type { PartialDeep, SetRequired } from "type-fest";

import type { Auction, Price, Product } from "@/Apollo/schema";

/**
 * What a listing is asking, resolved in one place (GEO-1).
 *
 * Four places had worked this out independently before this module
 * existed: `resolveAskingPrice` and `resolveAskingMoney` in the
 * comparable-alternatives ladder, the inline chain in the sold-page
 * schema, and the sold-page summary. The ordinary PDP's schema had it
 * backwards: `buildProductSchema` published `auction.minimum_bid` whenever
 * bidding was allowed, so on `the-club-lounge-chair-by-hans-kaufeld` the
 * JSON-LD offer said 350 while the price line said EUR 400.
 *
 * That was not an edge case. Auctions are created with `allow_bid` on and
 * `minimum_bid` at 70% of the asking price (`Auction.objects.create` in
 * the marketplace backend), and 312 of a 500-listing sample of buyable
 * inventory carry `allow_bid`, so the majority of live PDPs advertised a
 * price roughly 30% under the one on the page. Google treats a mismatched
 * `offers.price` as a structured-data error; an assistant just reads it as
 * the price.
 *
 * ## The precedence is the page's, not a new rule
 *
 * `getPriceInfo` builds the page's price line from `auction.buy_now_price`
 * and nothing else, and both price editors (the create-ad wizard and
 * chat's `ChatEditPrice`) read and write the auction's, so that is the first
 * candidate. The top-level `Product.buy_now_price` is deprecated in the
 * gateway schema ("Use product.auction.buy_now_price") and comes second:
 * it seeded the auction's when the auction was created and a 1000-listing
 * production sample found no listing where the two differed, but a
 * deprecation is a statement that they may, and a price drop is where it
 * would bite first. The bid floor comes last: `ProductDetailsInfoPrice`
 * prints it only as a secondary "bid from" line, and on a bid-only listing
 * it is the only number on the page.
 *
 * ## Types
 *
 * Derived from the generated schema through type-only imports. Type
 * imports are erased before anything resolves, so the specs run without
 * codegen; only enums used as VALUES (`Currency.Eur`) need the module.
 * `Pick` before `PartialDeep`, on the product and again on its auction,
 * keeps everything this function never reads (attributes, images, the
 * auction state) out of its parameter type, so any caller that carries
 * the price fields in schema shape can pass its own product type.
 * `sold_at` is named so a caller can hand over a sold auction; it is the
 * realised price and never a candidate.
 */

/** A `Price`, as loosely as a deep-partial product query returns one. */
export type AskingPriceLike = PartialDeep<Price> | null | undefined;

/** Anything that can carry an asking price: a PDP product, a search hit. */
export type AskingPriceProductLike =
  | (PartialDeep<Pick<Product, "buy_now_price">> & {
      auction?: PartialDeep<
        Pick<Auction, "buy_now_price" | "minimum_bid" | "sold_at">
      > | null;
    })
  | null
  | undefined;

/** An asking price that is safe to publish. */
export type AskingPrice = SetRequired<PartialDeep<Price>, "amount">;

/**
 * A candidate has to be a positive number to win.
 *
 * Zero is not a price a marketplace listing asks, and publishing
 * `price: "0"` in an offer would read as "free" to every consumer of the
 * schema, so a zero falls through to the next candidate exactly as a
 * missing value does. Written as a predicate so callers get an `amount`
 * they can do arithmetic on without a cast.
 */
const isPublishable = (candidate: AskingPriceLike): candidate is AskingPrice =>
  typeof candidate?.amount === "number" && candidate.amount > 0;

/**
 * The asking price as a Price, so the currency travels with the amount.
 *
 * Returns null when no candidate qualifies, which callers must treat as
 * "emit no price" rather than substituting one.
 */
export const resolveAskingPrice = (
  product: AskingPriceProductLike
): AskingPrice | null => {
  const candidates: AskingPriceLike[] = [
    product?.auction?.buy_now_price,
    product?.buy_now_price,
    product?.auction?.minimum_bid,
  ];

  return candidates.find(isPublishable) ?? null;
};

/**
 * The same answer as a bare amount, for callers that compare or sort on it
 * and have no use for the currency.
 */
export const resolveAskingPriceAmount = (product: AskingPriceProductLike) =>
  resolveAskingPrice(product)?.amount ?? null;
